import { addOns } from "@/mocks/booking";
import type { BookingState } from "@/hooks/useBooking";

type SummaryShape = {
  service: { title: string; basePrice: number };
  frequency: { label: string; discount: number };
  cleaner: { name: string; image: string } | null;
  bedroomSurcharge: number;
  addOnTotal: number;
  subtotal: number;
  discount: number;
  total: number;
};

type Props = {
  state: BookingState;
  summary: SummaryShape;
};

function formatDate(iso: string | null) {
  if (!iso) return "Not set";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

export default function BookingSummary({ state, summary }: Props) {
  return (
    <aside className="bg-background-50 border border-background-200/70 rounded-2xl p-6 sticky top-24">
      <p className="text-xs uppercase tracking-widest text-foreground-600 mb-3">Your booking</p>
      <h3 className="font-heading text-foreground-950 text-2xl font-medium mb-5">{summary.service.title}</h3>

      <div className="space-y-3 text-sm pb-5 border-b border-background-200">
        <Row label="Frequency" value={summary.frequency.label} />
        <Row label="Home" value={`${state.bedrooms} · ${state.bathrooms} bath`} />
        <Row label="Date" value={formatDate(state.date)} />
        <Row label="Time" value={state.time ?? "Not set"} />
        <Row
          label="Cleaner"
          value={summary.cleaner ? summary.cleaner.name : "Auto-match"}
        />
      </div>

      <div className="space-y-2 py-5 border-b border-background-200 text-sm">
        <Row label="Base" value={`$${summary.service.basePrice}`} muted />
        {summary.bedroomSurcharge > 0 && (
          <Row label="Home size" value={`+$${summary.bedroomSurcharge}`} muted />
        )}
        {state.selectedAddOns.length > 0 && (
          <div>
            <Row label={`Add-ons (${state.selectedAddOns.length})`} value={`+$${summary.addOnTotal}`} muted />
            <ul className="mt-1 pl-3 text-xs text-foreground-600 space-y-0.5">
              {state.selectedAddOns.map((id) => {
                const a = addOns.find((x) => x.id === id);
                return a ? <li key={id}>· {a.label}</li> : null;
              })}
            </ul>
          </div>
        )}
        {summary.discount > 0 && (
          <Row label={`Recurring discount`} value={`−$${summary.discount}`} accent />
        )}
      </div>

      <div className="flex items-baseline justify-between pt-5">
        <span className="text-foreground-700 text-sm">Estimated total</span>
        <span className="font-heading text-foreground-950 text-3xl font-medium">${summary.total}</span>
      </div>

      <p className="mt-4 text-xs text-foreground-600 leading-relaxed">
        You won't be charged until after your cleaner finishes the visit. Free reschedule up to 12 hrs before.
      </p>
    </aside>
  );
}

function Row({ label, value, muted, accent }: { label: string; value: string; muted?: boolean; accent?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className={`${muted ? "text-foreground-600" : "text-foreground-700"} text-sm`}>{label}</span>
      <span
        className={`text-sm font-medium ${
          accent ? "text-primary-700" : muted ? "text-foreground-800" : "text-foreground-950"
        }`}
      >
        {value}
      </span>
    </div>
  );
}